import { Link } from 'react-router-dom';

import Rich from '../components/Rich';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTeamContent, type TeamContent } from '../team/content';
import { upcomingMatch } from '../team/match';
import './Home.css';

interface MenuItem {
  name: string;
  description: string;
  price: string;
  featured?: boolean;
  badge?: string;
}

const MENU: MenuItem[] = [
  {
    name: 'The O.G. Mojito',
    description:
      'White rum, fresh mint, lime juice, cane sugar, soda water. The one that started it all.',
    price: '$12',
    featured: true,
    badge: 'Fan Favorite',
  },
  {
    name: 'Strawberry Sunset',
    description: 'Muddled strawberries, mint, rum, a splash of OJ, and a float of champagne.',
    price: '$14',
  },
  {
    name: 'Coconut Breeze',
    description:
      'Coconut rum, fresh mint, lime, pineapple juice, topped with toasted coconut flakes.',
    price: '$14',
  },
];

interface SportRuling {
  sport: string;
  status: string;
  tone: 'yes' | 'no' | 'maybe';
  note: string;
}

const SPORT_RULINGS: SportRuling[] = [
  {
    sport: 'Tennis',
    status: 'Approved',
    tone: 'yes',
    note: 'The formulation was designed around it. Obviously.',
  },
  {
    sport: 'Pickleball',
    status: 'Not Approved',
    tone: 'no',
    note: 'See above. See also: the entirety of the section above.',
  },
  {
    sport: 'Padel',
    status: 'Under Review',
    tone: 'maybe',
    note: 'The walls introduce variables we have not finished modeling.',
  },
  {
    sport: 'Badminton',
    status: 'Permitted',
    tone: 'yes',
    note: 'The shuttlecock is far too light to disturb anything. Enjoy yourself.',
  },
  {
    sport: 'Golf',
    status: 'Discouraged',
    tone: 'maybe',
    note: 'Not for our reasons. For yours.',
  },
  {
    sport: 'Cornhole',
    status: 'Enthusiastically Approved',
    tone: 'yes',
    note: 'Chuck considers this the ideal mojito delivery platform.',
  },
];

const GALLERY = [
  {
    src: '/images/1777f3eb-7e5e-46b9-959d-3acc9f7b56a3.jpg',
    alt: "Chuck's Mojito Shack exterior",
    large: true,
  },
  { src: '/images/536ef4bb-d97e-415a-b674-bc0e4b9edff9.jpg', alt: 'Signature mojito cocktail' },
  {
    src: '/images/fcb5c9b7-870a-4a4c-8c8c-72f36fd46e6e.jpg',
    alt: 'Chuck serving drinks at the bar',
  },
  { src: '/images/c25de36c-a2f3-44c6-945b-cad0daff9e1c.jpg', alt: 'Beachside tiki hut' },
];

export default function Home() {
  usePageTitle("Chuck's Mojito Shack — Best Southern Mojitos in the World");
  const { content: team } = useTeamContent();

  return (
    <>
      {/* ===== HERO SECTION ===== */}
      <section className="hero">
        <div className="hero-bg">
          <img
            src="/images/shack.jpg"
            alt="Chuck's Mojito Shack beachside tiki bar"
            className="hero-img"
          />
          <div className="hero-overlay"></div>
        </div>

        <div className="hero-content">
          <div className="hero-card">
            <img src="/images/Chuck's Mojito Shack.png" alt="" className="hero-badge" />
            <h1 className="hero-title">
              Chuck's
              <br />
              Mojito Shack
            </h1>
            <p className="hero-tagline">The Best Southern Mojitos in the World</p>
          </div>
          <div className="hero-divider">
            <span>&#x1F334;</span>
            <span className="divider-line"></span>
            <span>&#x1F379;</span>
            <span className="divider-line"></span>
            <span>&#x1F334;</span>
          </div>
          <p className="hero-sub">
            Beachside craft cocktails &middot; South Florida vibes &middot; Fresh-muddled daily
          </p>
          <a href="#menu" className="hero-cta">
            See the Menu
          </a>
        </div>

        <div className="hero-wave">
          <svg viewBox="0 0 1440 140" preserveAspectRatio="none">
            <path
              d="M0,80 C240,140 480,20 720,80 C960,140 1200,20 1440,80 L1440,140 L0,140 Z"
              fill="var(--sand-light)"
            />
          </svg>
        </div>
      </section>

      {/* ===== THE TENNIS TEAM'S CARD — written in /admin (Team → Home page card) ===== */}
      {team && <TennisCard content={team} />}

      {/* ===== SAMPLING EVENTS CARD ===== */}
      <section className="sampling-section" id="sampling">
        <div className="sampling-card">
          <span className="sampling-emoji" aria-hidden="true">
            🥤
          </span>
          <div className="sampling-body">
            <span className="sampling-tag">Missed It? That's On You.</span>
            <h2 className="sampling-title">Free Sampling Events</h2>
            <p className="sampling-text">
              A few weeks back, mojitos happened at the <strong>TLH Tennis Center</strong>. Free
              ones. If you weren't there, we're not going to make you feel bad about it &mdash; the
              people who were there will handle that for us. They're still talking about it. Loudly.
              In the parking lot.
            </p>
            <p className="sampling-text">
              The good news: Chuck does this again. The bad news: he refuses to announce it more
              than slightly in advance, because he believes anticipation is an ingredient. So bookmark
              this page, check it obsessively, and stay tuned &mdash; the next free sampling event
              gets posted right here and nowhere else.
            </p>
          </div>
          <a
            href="mailto:samples@chucksmojitoshack.com?subject=Tell%20Me%20About%20The%20Next%20Sampling"
            className="sampling-cta"
          >
            Tip Me Off
          </a>
        </div>
      </section>

      {/* ===== WELCOME STRIP ===== */}
      <section className="welcome-strip">
        <div className="welcome-inner">
          <div className="welcome-item">
            <span className="welcome-emoji">&#x1F378;</span>
            <div>
              <strong>Hand-Muddled</strong>
              <span>Fresh mint, lime &amp; cane sugar in every glass</span>
            </div>
          </div>
          <div className="welcome-item">
            <span className="welcome-emoji">&#x1F3D6;&#xFE0F;</span>
            <div>
              <strong>Beachside Vibes</strong>
              <span>Toes in the sand, drink in hand</span>
            </div>
          </div>
          <div className="welcome-item">
            <span className="welcome-emoji">&#x2600;&#xFE0F;</span>
            <div>
              <strong>South Florida Sun</strong>
              <span>Open 7 days a week, rain or shine</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== MENU SECTION ===== */}
      <section className="menu-section" id="menu">
        <div className="section-header">
          <span className="section-tag">What We Pour</span>
          <h2 className="section-title">The Mojito Menu</h2>
          <p className="section-sub">
            Every mojito starts with fresh-cut mint, hand-squeezed lime, and premium white rum. No
            shortcuts, ever.
          </p>
        </div>

        <div className="menu-grid">
          {MENU.map((item) => (
            <div key={item.name} className={`menu-card${item.featured ? ' featured' : ''}`}>
              {item.badge && <div className="menu-card-badge">{item.badge}</div>}
              <h3 className="menu-card-name">{item.name}</h3>
              <p className="menu-card-desc">{item.description}</p>
              <span className="menu-card-price">{item.price}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ===== SIMULATOR TEASER ===== */}
      <section className="mixer-teaser" id="mix">
        <div className="mixer-teaser-inner">
          <span className="mixer-teaser-emoji" aria-hidden="true">
            🍹
          </span>
          <div className="mixer-teaser-text">
            <span className="section-tag">Behind the Bar</span>
            <h2 className="section-title">Think You Can Make One?</h2>
            <p>
              Step behind the rail and build a Chuck's Mojito yourself. Muddle the mint, free-pour
              the rum, top it with soda — then serve it to Chuck and get scored against the spec he
              refuses to write down.
            </p>
          </div>
          <Link to="/mix" className="mixer-teaser-cta">
            Start Mixing
          </Link>
        </div>
      </section>

      {/* ===== ABOUT / STORY SECTION ===== */}
      <section className="about-section" id="about">
        <div className="about-inner">
          <div className="about-image-wrap">
            <img
              src="/images/roger.jpg"
              alt="The tennis crew at Chuck's Mojito Shack"
              className="about-image"
            />
            <div className="about-image-badge">Est. 2025</div>
          </div>
          <div className="about-text">
            <span className="section-tag">Our Story</span>
            <h2 className="section-title">A Drinking Team with a Tennis Problem</h2>
            <p>
              It started on the neighborhood tennis courts. A group of players realized their
              post-match hydration protocol was critically under-engineered. Standard electrolyte
              replenishment? Insufficient. Chuck hypothesized that a rum-based delivery system with
              a proprietary mint-lime suspension matrix would optimize both morale and recovery
              metrics. The hypothesis was correct.
            </p>
            <p>
              What began as a crude courtside R&amp;D operation has since evolved into a full-scale
              seasonal deployment. Chuck's Mojito Shack operates exclusively during the{' '}
              <strong>summer months</strong> &mdash; a narrow production window aligned with peak
              thermal conditions required for proper ingredient volatility. The exact formulation
              remains a closely guarded trade secret. Multiple attempts at reverse-engineering the
              mixture have failed. Independent lab analysis has returned only the classification:{' '}
              <em>"inconclusive &mdash; requires further sampling."</em>
            </p>
            <p>
              The proprietary blend leverages a multi-stage muddling algorithm, a cane-sugar
              calibration curve refined across 400+ batch iterations, and a rum-to-citrus ratio so
              precisely dialed-in that even a 0.3mL deviation is detectable by trained palates. No
              one outside the inner circle knows the full spec. Chuck doesn't write it down. He
              doesn't have to &mdash; it's committed to muscle memory, and muscle memory doesn't get
              subpoenaed.
            </p>
            <div className="about-callout">
              <span className="about-callout-icon">&#x1F3BE;</span>
              <p>
                <strong>Official team motto:</strong>{' '}
                <em>"We're a drinking team with a tennis problem."</em> Match results remain
                unverified. Mojito results speak for themselves.
              </p>
            </div>
            <div className="about-stats">
              <div className="stat">
                <span className="stat-number">400+</span>
                <span className="stat-label">Batch Iterations</span>
              </div>
              <div className="stat">
                <span className="stat-number">0</span>
                <span className="stat-label">Leaked Recipes</span>
              </div>
              <div className="stat">
                <span className="stat-number">???</span>
                <span className="stat-label">Secret Ingredients</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PICKLEBALL POSITION STATEMENT ===== */}
      <section className="pickleball-section" id="pickleball">
        <div className="pickleball-notice">
          <div className="pickleball-stripe" aria-hidden="true"></div>
          <div className="pickleball-inner">
            <span className="section-tag">A Matter of Policy</span>
            <h2 className="section-title">Official Position Statement: Pickleball</h2>

            <p className="pickleball-lead">
              Chuck's Mojito Shack does not condone, endorse, or formally recognize the consumption
              of its mojitos during, immediately before, or within sixty feet of a game of
              pickleball. This is not a rivalry. This is chemistry.
            </p>

            <p>
              The paddle report &mdash; that flat, percussive <em>POCK</em>, arriving on average
              every 1.4 seconds &mdash; sits at almost exactly the resonant frequency of a properly
              muddled mint leaf. Under sustained exposure the suspension matrix begins to
              delaminate. The mint separates. The lime goes it alone. Carbonation surrenders at four
              minutes and eleven seconds, a figure Chuck has verified on more occasions than he is
              willing to describe. A tennis ball, by contrast, produces a soft and forgiving{' '}
              <em>THOCK</em> that the mint finds reassuring.
            </p>

            <p>
              There is also the matter of proximity. A pickleball court is forty-four feet end to
              end. At no point during play are you more than a short stroll from your own drink.
              Tennis imposes a natural pacing mechanism &mdash; you have to actually run somewhere,
              and by the time you get back you've had a moment to reflect on your choices.
              Pickleball removes this safeguard entirely. Chuck regards that as an engineering
              failure, not a feature.
            </p>

            <figure className="pickleball-figure">
              <img
                src="/images/badpickleballers.jpg"
                alt="Split scene at the courts: glum pickleball players on the left, jubilant tennis players celebrating with mojitos on the right"
                loading="lazy"
              />
              <figcaption>
                Exhibit A. Left: pickleball, unaccompanied by mojitos, exactly as policy requires.
                Right: tennis, operating as designed.
              </figcaption>
            </figure>

            <div className="pickleball-callout">
              <span className="pickleball-callout-icon" aria-hidden="true">
                🏓
              </span>
              <p>
                <strong>For the avoidance of doubt:</strong> you are welcome at the Shack. Your
                paddle is welcome at the Shack. Your paddle is not welcome to hold a mojito.
              </p>
            </div>

            <h3 className="pickleball-table-heading">Racquet Sport Compatibility Ruling</h3>
            <div className="pickleball-table">
              {SPORT_RULINGS.map((ruling) => (
                <div key={ruling.sport} className={`ruling ruling-${ruling.tone}`}>
                  <span className="ruling-sport">{ruling.sport}</span>
                  <span className="ruling-status">{ruling.status}</span>
                  <span className="ruling-note">{ruling.note}</span>
                </div>
              ))}
            </div>

            <p className="pickleball-finalword">
              This position statement is not legal advice and has not been reviewed by anyone. It
              has, however, been tested extensively, which Chuck maintains is better.
            </p>
          </div>
        </div>
      </section>

      {/* ===== GALLERY SECTION ===== */}
      <section className="gallery-section" id="gallery">
        <div className="section-header">
          <span className="section-tag">The Vibe</span>
          <h2 className="section-title">Life at the Shack</h2>
        </div>

        <div className="gallery-grid">
          {GALLERY.map((image) => (
            <div
              key={image.src}
              className={`gallery-item${image.large ? ' gallery-item-large' : ''}`}
            >
              <img src={image.src} alt={image.alt} />
            </div>
          ))}
        </div>
      </section>

      {/* ===== SWAG SECTION ===== */}
      <section className="swag-section" id="swag">
        <div className="section-header">
          <span className="section-tag">Rep the Shack</span>
          <h2 className="section-title">Chuck's Mojito Swag</h2>
          <p className="section-sub">
            Coasters, stickers, t-shirts, hoodies &amp; more &mdash; take a piece of the Shack
            wherever you go.
          </p>
        </div>

        <div className="swag-card">
          <div className="swag-image-wrap">
            <img
              src="/images/swag.jpg"
              alt="Chuck's Mojito Shack merchandise — coasters, stickers, t-shirts and hoodies"
              className="swag-image"
            />
          </div>
          <div className="swag-details">
            <h3 className="swag-heading">Look Fresh. Sip Fresh.</h3>
            <p className="swag-text">
              Show the world you run with the Shack crew. Our merch lineup features the iconic
              Chuck's Mojito Shack logo on premium tees, cozy hoodies, die-cut stickers, and cork
              coasters that look just as good on your coffee table as they do at the bar.
            </p>
            <ul className="swag-list">
              <li>&#x1F455; T-Shirts &amp; Crop Tops</li>
              <li>&#x1F9E5; Hoodies &amp; Pullovers</li>
              <li>&#x1FA79; Stickers &amp; Decals</li>
              <li>&#x1F37B; Cork Coasters</li>
              <li>&#x1F58A;&#xFE0F; Pens &amp; Notebooks</li>
            </ul>
            <a
              href="mailto:orders@chucksmojitoshack.com?subject=Swag%20Order"
              className="swag-cta"
            >
              <span className="swag-cta-text">Order Now</span>
              <span className="swag-cta-icon">&#x1F334;</span>
            </a>
          </div>
        </div>
      </section>

      {/* ===== CTA BANNER ===== */}
      <section className="cta-banner">
        <div className="cta-inner">
          <h2 className="cta-title">
            Come Thirsty.
            <br />
            Leave Happy.
          </h2>
          <p className="cta-text">Grab a stool, kick off your shoes, and let Chuck do the rest.</p>
          <div className="cta-hours">
            <span>&#x1F31E; Mon–Thu: 11am – 11pm</span>
            <span>&#x1F319; Fri–Sun: 11am – 1am</span>
          </div>
        </div>
      </section>
    </>
  );
}

/** The tennis card: the copy comes from the Team content's homeCard; the
 *  "Next" and per-week result chips are built from the schedule, so the
 *  weekly Archive → Publish in /admin keeps them current. */
function TennisCard({ content }: { content: TeamContent }) {
  const card = content.homeCard;
  if (!card.show) return null;
  const next = card.resultChips ? upcomingMatch(content.schedule, new Date()) : undefined;
  const played = card.resultChips ? content.schedule.filter((m) => m.result) : [];
  return (
    <section className="announce-banner" role="status">
      <div className="announce-inner">
        <span className="announce-emoji" aria-hidden="true">
          {card.emoji}
        </span>
        <div className="announce-text">
          {card.headline && (
            <strong>
              <Rich text={card.headline} />
            </strong>
          )}
          {card.body && (
            <span>
              <Rich text={card.body} />
            </span>
          )}
          {(card.chip || next || played.length > 0) && (
            <ul className="announce-status" aria-label="Season status">
              {card.chip && <li className="announce-chip announce-chip-now">{card.chip}</li>}
              {next && (
                <li className={`announce-chip${card.chip ? '' : ' announce-chip-now'}`}>
                  Next &middot; {next.label} &middot; {next.away ? 'at' : 'vs'} {next.opponent}
                </li>
              )}
              {played.map((m) => {
                const won = m.result!.us > m.result!.them;
                return (
                  <li key={m.week} className={`announce-chip ${won ? 'announce-chip-won' : 'announce-chip-lost'}`}>
                    Wk {m.week} &middot; {won ? 'W' : 'L'} {m.result!.us}&ndash;{m.result!.them}
                  </li>
                );
              })}
            </ul>
          )}
          <Link to="/team" className="announce-update announce-link">
            🎾 Lineup, court times, season results and live scores on the Team page &rarr;
          </Link>
        </div>
      </div>
    </section>
  );
}
